/**
 * identity.mjs — the learner's enrolment identity, held on the device.
 *
 * Founder ruling, 2026-08-13: one identity across faces, many subscriptions under it.
 *
 * ── WHY THIS FILE HAD TO EXIST BEFORE PROGRESS COULD WORK AT ALL ───────────────────────────────
 * The shell read `localStorage['coc-learner']` in two places and NOTHING IN THE APP EVER WROTE IT.
 * So for every real user the ring drew nothing and Progress said "your progress appears once you
 * have attempted something" — permanently, no matter how much he attempted. It read like modesty
 * and was actually a dead branch. Identity is what closes it.
 *
 * ── THE TOKEN IS THE CREDENTIAL, AND THAT IS SAID OUT LOUD ─────────────────────────────────────
 * There is no password and no OTP (founder's standing ruling against user OTP emails). The id is
 * DERIVED from name + contact, so registering again on another device with the same two things
 * returns the same person and the same record — that is the recovery story, and it is the honest
 * one. It also means anyone who knows those two things can derive the id, which is why nothing here
 * calls this authentication.
 *
 * The legacy `coc-learner` key is still honoured for anyone mid-session on it. It is never written
 * again; a registered learner is keyed by his principal.
 */
const TOKEN_KEY = 'academy-principal-token';
const NAME_KEY = 'academy-principal-name';

const ls = (() => { try { return window.localStorage; } catch { return null; } })();

/* ── THE COOKIE, AND WHY A localStorage TOKEN WAS NOT ENOUGH ───────────────────────────────────
 * FOUNDER, 2026-08-18: "whole paper tabs have no mcq in many places ... even stability chapter 12".
 *
 * authHeaders() below carries the token on fetch() calls, and every API surface is served correctly
 * because of it. The whole-paper link is not a fetch — it is an <a href>, a top-level navigation,
 * and a browser attaches no custom header to one. So the server saw a stranger and refused a
 * chapter the reader had paid for. Every page route in the product has the same shape and the same
 * exposure; the paper is simply where it was first seen.
 *
 * So the token is mirrored into a cookie, which is the one credential a navigation does carry. It is
 * a MIRROR: localStorage stays the source of truth, the header still wins on the server whenever one
 * is present, and the cookie is read only when no header was sent. SameSite=Lax so it rides a
 * top-level GET the reader initiated and never a cross-site POST; Secure once we are on https.
 *
 * It is NOT HttpOnly, deliberately and with the cost stated: this file must be able to clear it on
 * sign-out, and a cookie the app cannot clear would outlive the sign-out that was supposed to end
 * the session. Anything script on this origin can reach, it can already reach in localStorage. */
const COOKIE_KEY = 'academy-principal';
const COOKIE_MAX_AGE = 180 * 24 * 60 * 60;
function writeCookie(token) {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(token)}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } catch { /* a document we cannot write to is not a reason to fail a sign-in */ }
}
function clearCookie() {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_KEY}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  } catch { /* as above */ }
}

export function identity() {
  const token = ls?.getItem(TOKEN_KEY) || '';
  if (!token) return null;
  /* MIRRORED ON READ, not only on sign-in. Every learner already signed in on this device holds a
     token and no cookie, and asking them all to sign in again to fix our bug is not a migration, it
     is a bill we send the user. Idempotent and cheap; the cookie can never disagree because it is
     rewritten from the token each time the token is read. */
  writeCookie(token);
  const principal = token.split('.')[0] || '';
  return { token, principal, name: ls?.getItem(NAME_KEY) || '' };
}

/** The legacy local id, for a device that has one and has not registered. Never created here. */
export const legacyLearner = () => ls?.getItem('coc-learner') || '';

/**
 * Headers for any call whose answer depends on WHO is asking. Returns {} when there is no identity,
 * so an anonymous caller is a normal request rather than a broken one — the feeler must work with
 * nobody signed in.
 */
/**
 * ── THE PSEUDONYM (founder, 2026-08-16: "count free readers, no name or contact") ─────────────
 *
 * Sixteen random hex characters, minted HERE by the browser, kept in this device's localStorage and
 * derived from nothing. Not from an address, not from a fingerprint, not from anything about the
 * person — so it cannot be turned back into anybody, and clearing site data destroys it for good.
 *
 * The easy alternative was to let the server hash the caller's IP, which enrolment.mjs already does
 * for `anon:` principals. It was refused: a hash of an address is still a derivative of an address
 * and identifies a household. The ruling said no name or contact, not "no name or contact but an
 * address is fine". ANKR enforces privacy in its products and does not get an exception for its own
 * metrics when the numbers would be flattering.
 *
 * `crypto.randomUUID` is not used because its output is not the shape the server accepts, and the
 * server refuses anything that is not — so a reader id can never become a field nobody designed.
 */
const READER_KEY = 'academy-reader';
export function readerId() {
  try {
    let v = ls?.getItem(READER_KEY) || '';
    if (!/^r-[0-9a-f]{16}$/.test(v)) {
      const b = new Uint8Array(8);
      (globalThis.crypto || {}).getRandomValues?.(b);
      v = 'r-' + [...b].map((n) => n.toString(16).padStart(2, '0')).join('');
      ls?.setItem(READER_KEY, v);
    }
    return v;
  } catch { return ''; }
}

/* ── THE DEMO TOKEN, READ BACK OUT OF ITS OWN COOKIE ───────────────────────────────────────────
 * FOUNDER, 2026-08-18: "once they register then demo is secondary and user enrolment is primary",
 * ruled in favour of a shareable /preview link.
 *
 * The server sets `academy-demo` and reads it on navigations. The SPA's own fetch() calls carry a
 * HEADER, and the header beats every cookie — so without this, a visitor following the link would
 * get chapters that open on navigation and an enrolment invitation in every panel the app fetches.
 * That is exactly the half-signed-in page the film hit, and it looks like a working session until
 * you read it.
 *
 * PRECEDENCE IS PRESERVED HERE TOO, and it must be: identity() is consulted first and this is
 * reached only when the reader has none of their own. The rule is the same on both sides of the
 * wire, which is the only way it stays true. */
const DEMO_COOKIE = 'academy-demo';
function demoToken() {
  try {
    for (const part of String(document.cookie || '').split(';')) {
      const i = part.indexOf('=');
      if (i < 0) continue;
      if (part.slice(0, i).trim() !== DEMO_COOKIE) continue;
      return decodeURIComponent(part.slice(i + 1).trim());
    }
  } catch { /* a document we cannot read is simply a reader with no demo */ }
  return '';
}
/** True when this browser is walking on the demo and has no identity of its own. */
export const isDemo = () => !identity() && !!demoToken();

export const authHeaders = () => {
  const id = identity();
  /* The demo is the FALLBACK, never the winner. A registered learner's token is returned above and
     this line is not reached. */
  if (!id) {
    const d = demoToken();
    if (d) return { 'x-reader': readerId(), 'x-principal': d };
  }
  /* The pseudonym travels on EVERY call, signed in or not. On a free chapter it is what gets
     counted; on a paid one the server ignores it — only a free read is counted, and that decision
     lives in the gate rather than here. Sending it unconditionally keeps this function honest about
     what it is: headers describing the caller, not a switch deciding what he is charged. */
  return { 'x-reader': readerId(), ...(id ? { 'x-principal': id.token } : {}) };
};

/**
 * Three doors, one shape. Each returns the whole response so the caller can act on what came back —
 * a recovery code to write down, or an `already_claimed` collision that is NOT a failed login and
 * must not be reported as one.
 */
async function post(api, route, payload, name) {
  const r = await fetch(`${api}/api/enrolment/${route}`, {
    method: 'POST',
    /* The pseudonym goes with the enrolment so the CONVERSION can be counted — the server marks
       that reader converted and does not write the principal beside it. Without this header the
       funnel is unmeasurable: reads would be counted and nobody would ever be seen to cross. */
    headers: { 'content-type': 'application/json', 'x-reader': readerId() },
    body: JSON.stringify(payload),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.token) {
    const e = new Error(d.error || 'the registry did not answer with an identity');
    e.alreadyClaimed = !!d.already_claimed;
    throw e;
  }
  ls?.setItem(TOKEN_KEY, d.token);
  if (name) ls?.setItem(NAME_KEY, String(name));
  writeCookie(d.token);
  return d;
}

/* `contact` is what DERIVES the principal and stays the identity input. The enrol form now sends
   the email as that contact plus a separate phone, and the server records both — see
   enrolment-contacts.mjs. Kept as an explicit fallback so a caller that sends only `contact` (the
   older shape, and every test that predates this) still registers. */
export const register = (api, { name, contact, email, phone, passphrase }) =>
  post(api, 'register', { name, contact: contact || email, email: email || contact, phone, passphrase }, name);

export const signin = (api, { name, contact, passphrase }) =>
  post(api, 'signin', { name, contact, passphrase }, name);

export const recover = (api, { name, contact, recoveryCode, passphrase }) =>
  post(api, 'recover', { name, contact, recovery_code: recoveryCode, passphrase }, name);

/**
 * ASK FOR A RESET LINK. Deliberately NOT routed through post(): that helper demands a token back
 * and throws without one, and this endpoint must never return a token — it answers the same
 * sentence whether or not the identity exists, because a route that answers differently is an
 * oracle for "is this person enrolled here". The caller shows what came back and nothing else.
 */
export async function requestReset(api, { name, contact }) {
  const r = await fetch(`${api}/api/enrolment/reset-request`, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name, contact }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'the registry could not take that request');
  return d;
}

/** Spend a reset link. This one DOES return a token, so it goes through the same door as the rest. */
export const resetConfirm = (api, { token, passphrase, name }) =>
  post(api, 'reset-confirm', { token, passphrase }, name);

/** Sign out clears the device, never the record — the record is derived and comes back on re-entry. */
export function signOut() {
  ls?.removeItem(TOKEN_KEY);
  ls?.removeItem(NAME_KEY);
  /* The cookie goes with them. A sign-out that clears the device but leaves the credential a page
     navigation uses would be a sign-out in the UI only — the reader would still be reading paid
     chapters, and would have been told he had left. */
  clearCookie();
}

export async function enrolments(api) {
  const r = await fetch(`${api}/api/enrolment`, { headers: authHeaders() });
  return r.json();
}
